import { Injectable } from '@angular/core';
import { ModelPage, ModelService } from '@shared/model-system';
import { User } from '@shared/models/user/user';
import { UserService } from '@shared/models/user/user.service';
import { ApiService } from '@shared/services/api.service';
import { chunk, extend } from 'lodash';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IDivision, Team } from './team';

/** Team service using the model system */
@Injectable()
export class TeamService extends ModelService<Team> {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public Type = Team;
  private readonly baseURI = '/platform/api/v2/teams';

  constructor(public api: ApiService, private readonly userService: UserService) {
    super();
  }

  public get(id: string): Observable<Team> {
    return this.api.get(`${this.baseURI}/${id}`).pipe(map((data) => this.create(data)));
  }

  public getPage(pageSize: number, name: string): Observable<ModelPage<Team>> {
    let params: { pageSize: number; expand: string; name?: string } = {
      pageSize,
      expand: 'entities.division'
    };
    if (name) {
      params.name = name;
    }
    const extendedParams = extend({}, params);

    return this.api.get(this.baseURI, extendedParams).pipe(
      map((data) => ({
        entities: data.entities ? data.entities.map((entity) => this.create(entity)) : [],
        previousUri: data.previousUri,
        nextUri: data.nextUri,
        pageSize
      }))
    );
  }

  public getPageWithUri(uri: string, pageSize: number): Observable<ModelPage<Team>> {
    return this.api.get(uri, { expand: 'entities.division' }).pipe(
      map((data) => ({
        entities: data.entities ? data.entities.map((entity) => this.create(entity)) : [],
        previousUri: data.previousUri,
        nextUri: data.nextUri,
        pageSize
      }))
    );
  }

  public createTeam(params: ICreateTeamParams): Observable<Team> {
    return this.api.post(this.baseURI, params).pipe(map((data) => this.create(data)));
  }

  public updateTeam({
    id,
    name,
    description,
    division
  }: ITeamServiceUpdateTeamOptions): Observable<Team> {
    const payload: { name: string; description: string; division?: IDivision } = {
      name,
      description
    };

    if (division) {
      payload.division = { id: division.id, name: division.name };
    }

    return this.api.patch(`${this.baseURI}/${id}`, payload).pipe(map((data) => this.create(data)));
  }

  public deleteTeam(id: string): Observable<null> {
    return this.api.delete(`${this.baseURI}/${id}`);
  }

  public getMembers(id: string, pageSize: number): Observable<ModelPage<User>> {
    const extendedParams = extend({ expand: 'entities' }, { pageSize });

    return this.api.get(`${this.baseURI}/${id}/members`, extendedParams).pipe(
      map((data) => ({
        entities: data.entities
          ? data.entities.map((entity) => this.userService.create(entity))
          : [],
        nextUri: data.nextUri,
        previousUri: data.previousUri,
        pageSize
      }))
    );
  }

  public getMembersWithUri(uri: string, pageSize: number): Observable<ModelPage<User>> {
    return this.api.get(uri, { expand: 'entities' }).pipe(
      map((data) => ({
        entities: data.entities
          ? data.entities.map((entity) => this.userService.create(entity))
          : [],
        nextUri: data.nextUri,
        previousUri: data.previousUri,
        pageSize
      }))
    );
  }

  public addMembers(id: string, userIDs: string[]): Observable<User[]> {
    const requests = chunk(userIDs, 25).map((ids: string[]) =>
      this.api
        .post(`${this.baseURI}/${id}/members`, { memberIds: ids })
        .pipe(map((data) => data.entities.map((entity) => this.userService.create(entity))))
    );

    return forkJoin(requests);
  }

  public removeMembers(id: string, userIDs: string[]): Observable<boolean> {
    const options = { params: { id: userIDs.join() } };

    return this.api.delete(`${this.baseURI}/${id}/members`, options);
  }
}

interface ITeamServiceUpdateTeamOptions {
  id: string;
  name: string;
  description?: string;
  division?: IDivision;
}

interface ICreateTeamParams {
  name: string;
  description?: string;
  division?: IDivision;
}
